import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowsUpDownIcon,
  CubeIcon,
  TagIcon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  StarIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { Badge } from '../common/Badge';
import Modal from '../common/Modal';
import { Checkbox } from '../common/Checkbox';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  ProductVariantOption,
  ProductVariantOptionFormData,
  PimAttribute,
  VariantMatrixData 
} from '../../types/pim';

interface ProductVariantsProps {
  productId: number;
  canEdit?: boolean;
}

export const ProductVariants: React.FC<ProductVariantsProps> = ({
  productId,
  canEdit = true
}) => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ProductVariantOption | null>(null);
  const [formData, setFormData] = useState<ProductVariantOptionFormData>({
    attributeId: 0,
    optionValue: '',
    skuSuffix: '',
    priceModifier: 0,
    stockQuantity: 0,
    isDefault: false,
    sortOrder: 0
  });

  // Matrix state
  const [matrixAttributes, setMatrixAttributes] = useState<number[]>([]);
  const [matrixValues, setMatrixValues] = useState<Record<number, string[]>>({});
  const [matrixDefaults, setMatrixDefaults] = useState<{
    priceModifier: number;
    stockQuantity: number;
    skuFormat: string;
  }>({
    priceModifier: 0,
    stockQuantity: 0,
    skuFormat: '{value}'
  });

  // Fetch variant options
  const { data: variantOptionsData, isLoading: loadingOptions } = useQuery({
    queryKey: ['pim-product-variant-options', productId],
    queryFn: () => pimService.getProductVariantOptions(productId),
    retry: false,
    onError: (error: any) => {
      console.error('Failed to load variant options:', error.message);
    }
  });

  // Fetch attributes for dropdown
  const { data: attributesData } = useQuery({
    queryKey: ['pim-attributes-for-variants'],
    queryFn: () => pimService.getAttributes({
      limit: 1000
    }),
    onError: (error: any) => {
      console.warn('Failed to load attributes:', error.message);
    }
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ProductVariantOptionFormData) => 
      pimService.createProductVariantOption(productId, data),
    onSuccess: () => {
      showNotification('Opcja wariantu została utworzona', 'success');
      queryClient.invalidateQueries(['pim-product-variant-options', productId]);
      handleCloseModals();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia opcji wariantu', 'error');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProductVariantOptionFormData> }) =>
      pimService.updateProductVariantOption(productId, id, data),
    onSuccess: () => {
      showNotification('Opcja wariantu została zaktualizowana', 'success');
      queryClient.invalidateQueries(['pim-product-variant-options', productId]);
      handleCloseModals();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas aktualizacji opcji wariantu', 'error');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (optionId: number) =>
      pimService.deleteProductVariantOption(productId, optionId),
    onSuccess: () => {
      showNotification('Opcja wariantu została usunięta', 'success');
      queryClient.invalidateQueries(['pim-product-variant-options', productId]);
      handleCloseModals();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas usuwania opcji wariantu', 'error');
    }
  });

  // Matrix creation mutation
  const matrixMutation = useMutation({
    mutationFn: (data: VariantMatrixData) => 
      pimService.createVariantMatrix(productId, data),
    onSuccess: () => {
      showNotification('Macierz wariantów została utworzona', 'success');
      queryClient.invalidateQueries(['pim-product-variant-options', productId]);
      handleCloseModals();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia macierzy wariantów', 'error');
    }
  });

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowMatrixModal(false);
    setSelectedOption(null);
    setFormData({
      attributeId: 0,
      optionValue: '',
      skuSuffix: '',
      priceModifier: 0,
      stockQuantity: 0,
      isDefault: false,
      sortOrder: 0
    });
    // Reset matrix state
    setMatrixAttributes([]);
    setMatrixValues({});
    setMatrixDefaults({
      priceModifier: 0,
      stockQuantity: 0,
      skuFormat: '{value}'
    });
  };

  const handleEdit = (option: ProductVariantOption) => {
    setSelectedOption(option);
    setFormData({
      attributeId: option.attribute_id,
      optionValue: option.option_value,
      skuSuffix: option.sku_suffix || '',
      priceModifier: option.price_modifier || 0,
      stockQuantity: option.stock_quantity || 0,
      isDefault: option.is_default,
      sortOrder: option.sort_order
    });
    setShowEditModal(true);
  };

  const handleDelete = (option: ProductVariantOption) => {
    setSelectedOption(option);
    setShowDeleteModal(true);
  };

  const handleSubmit = () => {
    if (selectedOption) {
      updateMutation.mutate({
        id: selectedOption.id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedOption) {
      deleteMutation.mutate(selectedOption.id);
    }
  };

  const getAttributeOptions = () => {
    if (!attributesData?.data) return [];
    
    // Filter to variant-suitable attributes (string, enum, number)
    const suitableAttributes = attributesData.data.filter(
      attr => ['string', 'enum', 'number'].includes(attr.type)
    );
    
    return suitableAttributes.map(attr => ({
      value: attr.id.toString(),
      label: `${attr.label} (${attr.type})`
    }));
  };

  const groupOptionsByAttribute = () => {
    if (!variantOptionsData?.data) return {};
    
    return variantOptionsData.data.reduce((acc, option) => {
      const attributeId = option.attribute_id;
      if (!acc[attributeId]) {
        acc[attributeId] = {
          attribute: option.attribute,
          options: []
        };
      }
      acc[attributeId].options.push(option);
      return acc;
    }, {} as Record<number, { attribute?: PimAttribute; options: ProductVariantOption[] }>);
  };

  const getStockBadge = (quantity?: number) => {
    if (!quantity || quantity === 0) {
      return <Badge variant="danger">Brak w magazynie</Badge>;
    }
    if (quantity < 10) {
      return <Badge variant="warning">Niski stan ({quantity})</Badge>;
    }
    return <Badge variant="success">W magazynie ({quantity})</Badge>;
  };

  // Matrix helper functions
  const addMatrixAttribute = (attributeId: number) => {
    if (!matrixAttributes.includes(attributeId)) {
      setMatrixAttributes(prev => [...prev, attributeId]);
      setMatrixValues(prev => ({ ...prev, [attributeId]: [''] }));
    }
  };

  const removeMatrixAttribute = (attributeId: number) => {
    setMatrixAttributes(prev => prev.filter(id => id !== attributeId));
    setMatrixValues(prev => {
      const newValues = { ...prev };
      delete newValues[attributeId];
      return newValues;
    });
  };

  const addMatrixValue = (attributeId: number) => {
    setMatrixValues(prev => ({
      ...prev,
      [attributeId]: [...(prev[attributeId] || []), '']
    }));
  };

  const removeMatrixValue = (attributeId: number, index: number) => {
    setMatrixValues(prev => ({
      ...prev,
      [attributeId]: prev[attributeId].filter((_, i) => i !== index)
    }));
  };

  const updateMatrixValue = (attributeId: number, index: number, value: string) => {
    setMatrixValues(prev => ({
      ...prev,
      [attributeId]: prev[attributeId].map((v, i) => i === index ? value : v)
    }));
  };

  const generateMatrixCombinations = () => {
    const attributes = matrixAttributes.filter(id => 
      matrixValues[id] && matrixValues[id].some(v => v.trim())
    );
    
    if (attributes.length === 0) return [];

    const combinations: Array<{
      attributes: Array<{ id: number; value: string; name: string }>;
      skuSuffix: string;
    }> = [];

    const generate = (index: number, current: Array<{ id: number; value: string; name: string }>) => {
      if (index === attributes.length) {
        const skuSuffix = current
          .map(attr => matrixDefaults.skuFormat.replace('{value}', attr.value))
          .join('-');
        combinations.push({ attributes: [...current], skuSuffix });
        return;
      }

      const attributeId = attributes[index];
      const attribute = attributesData?.data?.find(a => a.id === attributeId);
      const values = matrixValues[attributeId].filter(v => v.trim());

      for (const value of values) {
        current.push({ 
          id: attributeId, 
          value: value.trim(), 
          name: attribute?.name || `Attribute ${attributeId}` 
        });
        generate(index + 1, current);
        current.pop();
      }
    };

    generate(0, []);
    return combinations;
  };

  const handleCreateMatrix = () => {
    const combinations = generateMatrixCombinations();
    
    if (combinations.length === 0) {
      showNotification('Dodaj przynajmniej jeden atrybut z wartościami', 'warning');
      return;
    }

    const variants: ProductVariantOptionFormData[] = combinations.map((combo, index) => ({
      attributeId: combo.attributes[0].id, // Primary attribute for this combination
      optionValue: combo.attributes.map(a => a.value).join(' / '),
      skuSuffix: combo.skuSuffix,
      priceModifier: matrixDefaults.priceModifier,
      stockQuantity: matrixDefaults.stockQuantity,
      isDefault: index === 0,
      sortOrder: index
    }));

    matrixMutation.mutate({ variants });
  };

  const groupedOptions = groupOptionsByAttribute();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Warianty produktu</h3>
          <p className="text-sm text-gray-600">
            Zarządzaj opcjami wariantów jak rozmiar, kolor, itp.
          </p>
        </div>
        {canEdit && (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              icon={ArrowsUpDownIcon}
              onClick={() => setShowMatrixModal(true)}
              size="sm"
            >
              Macierz wariantów
            </Button>
            <Button
              icon={PlusIcon}
              onClick={() => setShowCreateModal(true)}
              size="sm"
            >
              Dodaj opcję
            </Button>
          </div>
        )}
      </div>

      {/* Variant Options */}
      {loadingOptions ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <p className="mt-2 text-sm text-gray-600">Ładowanie opcji wariantów...</p>
        </div>
      ) : Object.keys(groupedOptions).length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Brak opcji wariantów</h3>
            <p className="mt-1 text-sm text-gray-500">
              Dodaj opcje wariantów aby umożliwić klientom wybór różnych wersji produktu.
            </p>
            {canEdit && (
              <div className="mt-6">
                <Button
                  icon={PlusIcon}
                  onClick={() => setShowCreateModal(true)}
                >
                  Dodaj pierwszą opcję
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.values(groupedOptions).map(({ attribute, options }) => (
            <Card key={attribute?.id}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <TagIcon className="h-5 w-5" />
                      <span>{attribute?.label}</span>
                      <Badge variant="secondary" size="sm">{attribute?.type}</Badge>
                    </CardTitle>
                    {attribute?.description && (
                      <p className="text-sm text-gray-600 mt-1">{attribute.description}</p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {options.length} opcji
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {options.map((option) => (
                    <div
                      key={option.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">
                            {option.option_value}
                          </span>
                          {option.is_default && (
                            <Badge variant="primary" icon={StarIcon} size="sm">
                              Domyślna
                            </Badge>
                          )}
                          {!option.is_active && (
                            <Badge variant="secondary" size="sm">
                              Nieaktywna
                            </Badge>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEdit(option)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(option)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 text-sm">
                        {option.sku_suffix && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Przyrostek SKU:</span>
                            <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                              {option.sku_suffix}
                            </span>
                          </div>
                        )}
                        
                        {option.price_modifier !== 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Modyfikator ceny:</span>
                            <div className="flex items-center space-x-1">
                              <CurrencyDollarIcon className="h-4 w-4" />
                              <span className={`font-medium ${
                                option.price_modifier > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {option.price_modifier > 0 ? '+' : ''}{option.price_modifier}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Stan magazynowy:</span>
                          {getStockBadge(option.stock_quantity)}
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Pozycja sortowania:</span>
                          <span>{option.sort_order}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={handleCloseModals}
        title={selectedOption ? 'Edytuj opcję wariantu' : 'Dodaj opcję wariantu'}
      >
        <div className="space-y-4">
          <Select
            label="Atrybut *"
            value={formData.attributeId.toString()}
            onChange={(value) => setFormData(prev => ({ 
              ...prev, 
              attributeId: parseInt(value) 
            }))}
            options={[
              { value: '0', label: 'Wybierz atrybut' },
              ...getAttributeOptions()
            ]}
            required
          />

          <Input
            label="Wartość opcji *"
            value={formData.optionValue}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              optionValue: e.target.value 
            }))}
            placeholder="np. Czerwony, XL, 64GB"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Przyrostek SKU"
              value={formData.skuSuffix}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                skuSuffix: e.target.value 
              }))}
              placeholder="np. -RED, -XL"
              help="Dodawany do SKU produktu"
            />

            <Input
              label="Modyfikator ceny"
              type="number"
              step="0.01"
              value={formData.priceModifier}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                priceModifier: parseFloat(e.target.value) || 0 
              }))}
              placeholder="0.00"
              help="Zmiana ceny (+/-)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Stan magazynowy"
              type="number"
              min="0"
              value={formData.stockQuantity}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                stockQuantity: parseInt(e.target.value) || 0 
              }))}
              placeholder="0"
            />

            <Input
              label="Pozycja sortowania"
              type="number"
              min="0"
              value={formData.sortOrder}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                sortOrder: parseInt(e.target.value) || 0 
              }))}
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              checked={formData.isDefault}
              onChange={(checked) => setFormData(prev => ({ 
                ...prev, 
                isDefault: checked 
              }))}
            />
            <label className="text-sm font-medium text-gray-700">
              Opcja domyślna
            </label>
            <span className="text-sm text-gray-500">
              Wybierana automatycznie
            </span>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleCloseModals}
            >
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formData.attributeId || !formData.optionValue || 
                       createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Zapisywanie...'
                : selectedOption ? 'Aktualizuj' : 'Utwórz'
              }
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleCloseModals}
        title="Usuń opcję wariantu"
      >
        <div className="space-y-4">
          <p>
            Czy na pewno chcesz usunąć opcję wariantu "{selectedOption?.option_value}"?
            Ta operacja jest nieodwracalna.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={handleCloseModals}
            >
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Matrix Creation Modal */}
      <Modal
        isOpen={showMatrixModal}
        onClose={handleCloseModals}
        title="Macierz wariantów"
        size="xl"
      >
        <div className="space-y-6">
          <div>
            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Szybko utwórz kombinacje wariantów wybierając atrybuty i ich wartości. 
                System automatycznie wygeneruje wszystkie możliwe kombinacje.
              </p>
              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Krok {matrixAttributes.length === 0 ? '1' : 
                      generateMatrixCombinations().length === 0 ? '2' : '3'} z 3
              </div>
            </div>
            
            {/* Add Attribute */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-medium text-gray-900 mb-3">Wybierz atrybuty wariantów</h4>
              <Select
                value=""
                onChange={(value) => {
                  if (value) addMatrixAttribute(parseInt(value));
                }}
                options={[
                  { value: '', label: 'Dodaj atrybut...' },
                  ...getAttributeOptions().filter(opt => 
                    !matrixAttributes.includes(parseInt(opt.value))
                  )
                ]}
              />
            </div>

            {/* Attributes and Values */}
            {matrixAttributes.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Wartości atrybutów</h4>
                
                {matrixAttributes.map(attributeId => {
                  const attribute = attributesData?.data?.find(a => a.id === attributeId);
                  return (
                    <div key={attributeId} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-800 flex items-center gap-2">
                          <TagIcon className="h-4 w-4" />
                          {attribute?.name}
                        </h5>
                        <button
                          onClick={() => removeMatrixAttribute(attributeId)}
                          className="text-red-500 hover:text-red-700"
                          aria-label={`Usuń atrybut ${attribute?.name}`}
                          title={`Usuń atrybut ${attribute?.name}`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {(matrixValues[attributeId] || []).map((value, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              placeholder={`Wartość ${index + 1}`}
                              value={value}
                              onChange={(e) => updateMatrixValue(attributeId, index, e.target.value)}
                              className="flex-1"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && value.trim()) {
                                  e.preventDefault();
                                  addMatrixValue(attributeId);
                                }
                              }}
                            />
                            <button
                              onClick={() => removeMatrixValue(attributeId, index)}
                              className="text-gray-400 hover:text-red-500"
                              aria-label={`Usuń wartość ${value || `${index + 1}`}`}
                              title="Usuń wartość"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center justify-between">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addMatrixValue(attributeId)}
                            icon={PlusIcon}
                          >
                            Dodaj wartość
                          </Button>
                          <div className="text-xs text-gray-500">
                            Tip: Naciśnij Enter aby dodać kolejną wartość
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Matrix Defaults */}
            {matrixAttributes.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Domyślne ustawienia</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Format SKU"
                    value={matrixDefaults.skuFormat}
                    onChange={(e) => setMatrixDefaults(prev => ({ 
                      ...prev, 
                      skuFormat: e.target.value 
                    }))}
                    placeholder="{value}"
                    help="Użyj {value} dla wartości atrybutu"
                  />
                  <Input
                    label="Modyfikator ceny"
                    type="number"
                    step="0.01"
                    value={matrixDefaults.priceModifier}
                    onChange={(e) => setMatrixDefaults(prev => ({ 
                      ...prev, 
                      priceModifier: parseFloat(e.target.value) || 0 
                    }))}
                  />
                  <Input
                    label="Stan magazynowy"
                    type="number"
                    min="0"
                    value={matrixDefaults.stockQuantity}
                    onChange={(e) => setMatrixDefaults(prev => ({ 
                      ...prev, 
                      stockQuantity: parseInt(e.target.value) || 0 
                    }))}
                  />
                </div>
              </div>
            )}

            {/* Preview */}
            {matrixAttributes.length > 0 && (
              <div className="bg-green-50 p-4 rounded-lg" role="region" aria-label="Podgląd kombinacji">
                <h4 className="font-medium text-gray-900 mb-3">Podgląd kombinacji</h4>
                <div className="text-sm text-gray-600 mb-2">
                  Zostanie utworzonych: <strong>{generateMatrixCombinations().length} wariantów</strong>
                </div>
                {generateMatrixCombinations().length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {generateMatrixCombinations().slice(0, 10).map((combo, index) => (
                      <div key={index} className="text-xs bg-white p-2 rounded border">
                        {combo.attributes.map(attr => attr.value).join(' / ')} 
                        <span className="text-gray-500 ml-2">(SKU: {combo.skuSuffix})</span>
                      </div>
                    ))}
                    {generateMatrixCombinations().length > 10 && (
                      <div className="text-xs text-gray-500">
                        ... i {generateMatrixCombinations().length - 10} więcej
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleCloseModals}
            >
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateMatrix}
              disabled={matrixAttributes.length === 0 || matrixMutation.isLoading}
              icon={ArrowsUpDownIcon}
            >
              {matrixMutation.isLoading ? 'Tworzenie...' : 'Utwórz macierz'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};